import {
  Column,
  ColumnOrderState,
  flexRender,
  Header as ReactTableHeader,
  HeaderGroup,
  RowData,
  useReactTable,
} from '@tanstack/react-table';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { ConnectDragPreview, ConnectDragSource, ConnectDropTarget, DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useTable } from './table-state-provider';
import { getStickyStyles } from './utils';
import { ArrowDown, ArrowUp, ColumnMeta, EllipsisVerticalIcon, Popover } from '../../index';

const iconClasses = 'mb-0.5 ml-1 h-4 w-4 inline fill-current';

// Generate menu items based on header options and current sort order
// eslint-disable-next-line max-lines-per-function
function sortMenu(
  canSort: boolean,
  sortDirection: false | 'asc' | 'desc',
  header: ReactTableHeader<unknown, unknown>,
  canHide: boolean,
  setVisible: Dispatch<SetStateAction<boolean>>
) {
  const options = [];
  if (canSort) {
    const sort = (sortMode: 'asc' | 'desc' | 'clear') => {
      setVisible(false);
      if (sortMode === 'clear') header.column.clearSorting();
      else header.column.toggleSorting(sortMode === 'desc');
    };
    if (sortDirection === 'desc' || sortDirection === false) {
      options.push(
        <button key="sort_asc" className="table-action-item" onClick={() => sort('asc')}>
          Sort ascending
        </button>
      );
    }
    if (sortDirection === 'asc' || sortDirection === false) {
      options.push(
        <button key="sort_desc" className="table-action-item" onClick={() => sort('desc')}>
          Sort descending
        </button>
      );
    }
    if (sortDirection !== false) {
      options.push(
        <button key="clear_sort" className="table-action-item" onClick={() => sort('clear')}>
          Clear sort
        </button>
      );
    }
  }
  if (canHide) {
    options.push(
      <button
        key="hide_field"
        className="table-action-item"
        onClick={() => {
          setVisible(false);
          header.column.toggleVisibility();
        }}
      >
        Hide field
      </button>
    );
  }
  return options;
}

// Component to handle header options e.g. sort, hide
const SortColumnContent = ({ header }: { header: ReactTableHeader<unknown, unknown> }) => {
  const canSort = header.column.getCanSort();
  const canHide = header.column.getCanHide();
  const sortDirection = header.column.getIsSorted();
  const [visible, setVisible] = useState(false);
  const menu = useMemo(
    () => sortMenu(canSort, sortDirection, header, canHide, setVisible),
    [canHide, canSort, header, sortDirection]
  );
  return (
    <Popover
      open={visible}
      onOpenChange={setVisible}
      content={<div className="table-action-container">{menu}</div>}
      showArrow={false}
      arrowPointAtCenter={false}
      placement="bottom-end"
      innerContentClassName="text-sm"
      offsetOptions={{ crossAxis: 1, mainAxis: 0 }}
    >
      <div className="group/th header-th-content flex">
        <div className="flex flex-1">
          <CellContent header={header} />
          <div className="w-2">
            {sortDirection !== false &&
              (sortDirection == 'asc' ? <ArrowUp className={iconClasses} /> : <ArrowDown className={iconClasses} />)}
          </div>
        </div>
        <div className="invisible inline w-2 pl-4 pr-2 pt-1 group-hover/th:visible">
          {EllipsisVerticalIcon({ className: 'h-4 w-4', title: 'EllipsisVertical' })}
        </div>
      </div>
    </Popover>
  );
};

const CellContent = ({ header }: { header: ReactTableHeader<unknown, unknown> }) => {
  return <>{header.isPlaceholder ? undefined : flexRender(header.column.columnDef.header, header.getContext())}</>;
};

// Display cell content, sort icons, drag-n-drop icon.
//  Optionally this component accepts dragProps needed for header drag-n-drop.
//  If dragProps passed it sets appropriate references and displays 🟰 icon.
const Cell = ({
  header,
  index,
  dragProps,
}: {
  header: ReactTableHeader<unknown, unknown>;
  index: number;
  dragProps?: {
    dropRef: ConnectDropTarget;
    dragRef: ConnectDragSource;
    previewRef: ConnectDragPreview;
    isDragging: boolean;
  };
}) => {
  const { stickyColumns } = useTable();
  let classes = stickyColumns?.includes(header.column.id) ? ' header-th-sticky' : ' header-th';
  if ((header.column.columnDef.meta as ColumnMeta | undefined)?.classes) {
    classes += ' ' + (header.column.columnDef.meta as ColumnMeta).classes;
  }
  const styles = getStickyStyles(header.column);
  const canSort = header.column.getCanSort();
  const canHide = header.column.getCanHide();
  const sortDirection = header.column.getIsSorted();
  return (
    <th
      style={{ opacity: dragProps?.isDragging ? 0.5 : 1, ...styles }}
      ref={dragProps?.dropRef}
      key={`${header.column.id}_${index}`}
      scope="col"
      className={classes}
    >
      <div className="flex" ref={dragProps?.previewRef}>
        <div className={`table-cell-text flex flex-1 flex-col ${sortDirection ? 'text-jungle-800' : 'text-gray-900'}`}>
          {canSort || canHide ? (
            <SortColumnContent header={header}></SortColumnContent>
          ) : (
            <div className="header-th-content flex">
              <CellContent header={header} />
            </div>
          )}
        </div>
        {dragProps && (
          <button className="pr-2" ref={dragProps?.dragRef}>
            🟰
          </button>
        )}
      </div>
    </th>
  );
};

const reorderColumn = (draggedColumnId: string, targetColumnId: string, columnOrder: string[]): ColumnOrderState => {
  columnOrder.splice(
    columnOrder.indexOf(targetColumnId),
    0,
    columnOrder.splice(columnOrder.indexOf(draggedColumnId), 1)[0]
  );
  return [...columnOrder];
};

// This component initiated references needed for drag-n-drop
const DraggableColumnHeader = <TData,>({
  table,
  header,
  index,
}: {
  table: ReturnType<typeof useReactTable<TData>>;
  header: ReactTableHeader<unknown, unknown>;
  index: number;
}) => {
  const [, dropRef] = useDrop({
    accept: 'column',
    drop: (draggedColumn: Column<TData>) => {
      const newColumnOrder = reorderColumn(
        draggedColumn.id,
        header.column.id,
        table?.getVisibleLeafColumns().map((col) => col.id) || []
      );
      table?.setColumnOrder(newColumnOrder);
    },
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    item: () => header.column,
    type: 'column',
  });
  return <Cell header={header} index={index} dragProps={{ dropRef, dragRef, previewRef, isDragging }} />;
};

const Header = () => {
  const { table, stickyColumns, enableColumnOrdering } = useTable();
  const canOrderHeader = useCallback(
    (header: ReactTableHeader<RowData, unknown>) => enableColumnOrdering && !stickyColumns?.includes(header.column.id),
    [enableColumnOrdering, stickyColumns]
  );
  return (
    <DndProvider backend={HTML5Backend}>
      <thead>
        {table?.getHeaderGroups().map((headerGroup: HeaderGroup<RowData>) => (
          <tr key={`header_${headerGroup.id}`} className={'header-tr'}>
            {headerGroup.headers.map((header, index) => {
              return canOrderHeader(header) ? (
                <DraggableColumnHeader table={table} key={`${header.id}_dnd`} header={header} index={index} />
              ) : (
                <Cell header={header} key={`${header.id}_plain_cell`} index={index} />
              );
            })}
          </tr>
        ))}
      </thead>
    </DndProvider>
  );
};

export default Header;
